import React, {useEffect, useState} from 'react';
import {Controller} from "react-hook-form";
import debounce from 'lodash/debounce'
import {Autocomplete, Checkbox, Chip, TextField} from "@mui/material";
import {campaignServices} from "../../service/campaignService";
import {CheckBox, CheckBoxOutlineBlank} from "@mui/icons-material";

const icon = <CheckBoxOutlineBlank fontSize="small"/>;
const checkedIcon = <CheckBox fontSize="small"/>;
export const MAX_SELECTED_CAMPAIGNS = 50;

function CampaignSelect(
    {
        control,
        updateFunc = null,
        trafficSourceId = "",
        validate = null,
        slotProps,
    }
) {

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        async function fetchData(): void {
            try {
                setLoading(true);
                const response = await campaignServices.read({name: search, trafficSourceId});
                setOptions(
                    response.campaigns.map(item => {
                        return {
                            value: item?.id,
                            label: item?.name,
                        }
                    })
                );
            } catch (e) {
                console.log("Error: ", e?.response?.data?.error);
            } finally {
                setLoading(false);
            }
        }

        if (open && (search.length === 0 || search.length > 0)) {
            fetchData();
        }

    }, [search, open, trafficSourceId]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
            setSearch("");
        }
    }, [open]);

    const handleSearch = debounce(searchTerm => {
        setSearch(searchTerm);
    }, 800);

    return (
        <Controller
            render={({field: {onChange, value = [], ...props}, fieldState}) => (
                <Autocomplete
                    slotProps={slotProps}
                    multiple
                    disableCloseOnSelect={true}
                    id="campaign-select"
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    getOptionLabel={(option) => option?.label}
                    options={options}
                    value={value}
                    getOptionDisabled={(option) => value.length >= MAX_SELECTED_CAMPAIGNS &&
                        !value.some((selected) => selected.value === option.value)}
                    loading={loading}
                    renderOption={(renderProps, option, {selected, index}) => (
                        <li {...renderProps} key={`${option?.value || 'camp'}-${index}`}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                sx={{mr: 1}}
                                checked={selected}
                                color="primary"
                            />
                            {option.label}
                        </li>
                    )}
                    renderValue={(selected, getTagProps) =>
                        selected?.map((option, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return (
                                <Chip
                                    key={key}
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    label={option?.label || option}
                                    {...tagProps}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            autoFocus
                            {...params}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || `${value.length} / ${MAX_SELECTED_CAMPAIGNS} campaigns selected`}
                            variant="outlined"
                            size="small"
                            margin="dense"
                            placeholder="Search and select campaigns"
                            value={search}
                            onChange={e => handleSearch(e.target.value)}
                            label="Campaign*"
                            slotProps={{inputLabel: {shrink: true}}}
                        />
                    )}
                    onChange={(e, data) => {
                        if (data.length > MAX_SELECTED_CAMPAIGNS && data.length > value.length) {
                            return;
                        }
                        if (updateFunc != null) {
                            updateFunc(data);
                        }
                        onChange(data);
                    }}
                    {...props}
                />
            )}
            rules={{
                required: "Select at least 1 campaign",
                validate: {
                    maxCampaigns: (value) => value.length <= MAX_SELECTED_CAMPAIGNS ||
                        `Select up to ${MAX_SELECTED_CAMPAIGNS} campaigns. Remove extra campaigns to continue.`,
                    ...(validate ? {custom: validate} : {})
                },
            }}
            name="campaign"
            control={control}
            defaultValue={[]}
        />
    );
}

export default CampaignSelect;
